/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

export interface Sl4wGains {
  originalGain: number;
  enhancedSpeechGain: number;
  isolatedSpeechGain: number;
  backgroundGain: number;
  musicGain: number;
}

export interface ComputeSl4wGainsParams {
  enhancePercent: number;
  backgroundPercent: number;
  musicPercent: number;
  bypassMixing: boolean;
  enhanceMuted?: boolean;
  backgroundMuted?: boolean;
  musicMuted?: boolean;
}

/**
 * Compute gain values for SL4W 8-channel audio mixing.
 *
 * SL4W channel layout:
 * - c0-c1: Original (stereo) - not used when mixing is enabled
 * - c2: Enhanced Speech (mono)
 * - c3: Isolated Speech (mono)
 * - c4-c5: Music (stereo)
 * - c6-c7: Background (stereo)
 */
export const computeSl4wGains = ({
  enhancePercent,
  backgroundPercent,
  musicPercent,
  bypassMixing,
  enhanceMuted = false,
  backgroundMuted = false,
  musicMuted = false,
}: ComputeSl4wGainsParams): Sl4wGains => {
  if (bypassMixing) {
    return {
      originalGain: 1.0,
      enhancedSpeechGain: 0.0,
      isolatedSpeechGain: 0.0,
      backgroundGain: 0.0,
      musicGain: 0.0,
    };
  }

  // Enhanced/Isolated speech: equal gain crossfade (same as ESv2)
  let enhancedGain = enhancePercent / 100.0;
  let isolatedGain = 1 - enhancedGain;

  // Background gain: logarithmic curve (same as ESv2)
  let backgroundGain = gainForStrengthPercent(backgroundPercent);

  // Music gain: logarithmic curve (same formula as background)
  let musicGain = gainForStrengthPercent(musicPercent);

  // Original is not used when mixing is enabled
  const originalGain = 0.0;

  // Apply muting
  if (enhanceMuted) {
    enhancedGain = 0.0;
    isolatedGain = 0.0;
  }

  if (backgroundMuted) {
    backgroundGain = 0.0;
  }

  if (musicMuted) {
    musicGain = 0.0;
  }

  return {
    originalGain,
    enhancedSpeechGain: enhancedGain,
    isolatedSpeechGain: isolatedGain,
    backgroundGain,
    musicGain,
  };
};

/**
 * Convert a 0-100 strength percent to a 0-1 amplitude gain.
 * Uses a logarithmic curve that gives -9dB (approximately half loudness) at 50%.
 */
const gainForStrengthPercent = (percent: number): number => {
  if (percent === 0.0) {
    return 0.0;
  } else if (percent === 100.0) {
    return 1.0;
  } else {
    const removalStrength = 1.0 - percent / 100.0;
    // -36dB at 0%, quadratic curve gives -9dB at 50%
    const minDBFS = -36.0;
    const dB = minDBFS * Math.pow(removalStrength, 2.0);
    return Math.pow(10, dB / 20);
  }
};
